import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SignatureCapture } from '@/components/documents/SignatureCapture';
import { generateDocumentPdf } from '@/lib/pdfGeneration';
import { toast } from 'sonner';
import { Loader2, FileText, PenTool, RefreshCw, AlertTriangle, CheckCircle, Calendar } from 'lucide-react';
import { format, differenceInDays, isPast, addDays } from 'date-fns';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

interface AttestationRenewalProps {
  governanceDocumentId: string;
  onRenewalComplete?: () => void;
}

interface GovernanceDocument {
  id: string;
  una_id: string;
  document_title: string;
  document_type: string;
  document_category: string;
  expiration_date: string | null;
  version: number | null;
  is_current_version: boolean | null;
  content_html: string | null;
}

interface UnaData {
  id: string;
  name: string;
  primary_officer_role: string | null;
}

interface ProfileData {
  id: string;
  full_name: string;
}

export function AttestationRenewal({ governanceDocumentId, onRenewalComplete }: AttestationRenewalProps) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [document, setDocument] = useState<GovernanceDocument | null>(null);
  const [unaData, setUnaData] = useState<UnaData | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [templateContent, setTemplateContent] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the governance document
        const { data: govDoc, error: govError } = await supabase
          .from('governance_documents')
          .select('*')
          .eq('id', governanceDocumentId)
          .single();

        if (govError) throw govError;
        setDocument(govDoc);

        // Fetch UNA data
        const { data: una, error: unaError } = await supabase
          .from('unas')
          .select('id, name, primary_officer_role')
          .eq('id', govDoc.una_id)
          .single();

        if (unaError) throw unaError;
        setUnaData(una);

        // Fetch user profile
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;
        setProfileData(profile);

        // Fetch document template for attestation
        const { data: template, error: templateError } = await supabase
          .from('document_templates')
          .select('template_content')
          .eq('template_name', 'stewards_declaration_of_belief_and_conduct')
          .eq('is_active', true)
          .single();

        if (templateError) {
          console.warn('Could not fetch template, using stored content');
        } else {
          setTemplateContent(template.template_content);
        }
      } catch (error) {
        console.error('Error fetching renewal data:', error);
        toast.error('Failed to load renewal data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [governanceDocumentId]);

  const getExpirationStatus = () => {
    if (!document?.expiration_date) return { status: 'no_expiration', label: 'No Expiration', color: 'secondary' };
    
    const expirationDate = new Date(document.expiration_date);
    const daysUntilExpiration = differenceInDays(expirationDate, new Date());
    
    if (isPast(expirationDate)) {
      return { status: 'expired', label: 'Expired', color: 'destructive', days: Math.abs(daysUntilExpiration) };
    } else if (daysUntilExpiration <= 30) {
      return { status: 'expiring_soon', label: 'Expiring Soon', color: 'warning', days: daysUntilExpiration };
    } else {
      return { status: 'valid', label: 'Valid', color: 'default', days: daysUntilExpiration };
    }
  };

  const getPopulatedHtml = (): string => {
    if (!templateContent || !unaData || !profileData) return '';

    let html = templateContent;
    html = html.replace(/\{\{una_name\}\}/g, unaData.name || '');
    html = html.replace(/\{\{signer_name\}\}/g, profileData.full_name || '');
    html = html.replace(/\{\{signature_date\}\}/g, format(new Date(), 'MMMM d, yyyy'));

    return html;
  };

  const wrapInEcclesiasticalStyles = (bodyHtml: string): string => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page { size: Letter; margin: 0.75in; }
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      background: white;
      margin: 0;
      padding: 0;
    }
    h1 { font-size: 18pt; font-weight: bold; margin: 0 0 20pt 0; text-align: center; text-transform: uppercase; letter-spacing: 0.12em; border-bottom: 2px solid #c9a961; padding-bottom: 14pt; }
    h2 { font-size: 13pt; font-weight: bold; margin: 24pt 0 10pt 0; border-bottom: 1px solid #bfbfbf; padding-bottom: 6pt; color: #262626; }
    h3 { font-size: 12pt; font-weight: bold; font-style: italic; margin: 18pt 0 8pt 0; color: #333; }
    p { margin: 0 0 14pt 0; font-size: 11pt; text-align: justify; }
    li { margin: 8pt 0; }
    ul, ol { margin: 14pt 0; padding-left: 28pt; }
    .signature-section { margin-top: 40pt; padding-top: 24pt; border-top: 1px solid #ccc; }
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
  };

  const embedSignatureInHtml = (html: string, signatureBase64: string): string => {
    const signatureHtml = `
      <div style="margin-top: 10px;">
        <img src="${signatureBase64}" alt="Signature" style="max-height: 60px; max-width: 300px;" />
        <p style="font-size: 10pt; color: #666; margin-top: 4px;">
          Electronically signed on ${format(new Date(), 'MMMM d, yyyy at h:mm a')}
        </p>
      </div>
    `;

    return html.replace(
      /<div[^>]*class="signature-placeholder"[^>]*data-signer-id="treasury_secretary"[^>]*><\/div>/,
      signatureHtml
    );
  };

  const handleRenewal = async (signatureData: string) => {
    if (!document || !unaData || !profileData) return;

    setProcessing(true);

    try {
      const newVersion = (document.version || 1) + 1;
      const populatedHtml = getPopulatedHtml();
      const signedHtml = embedSignatureInHtml(populatedHtml, signatureData);
      const styledSignedHtml = wrapInEcclesiasticalStyles(signedHtml);

      // 1. Create new generated_documents record
      const { data: docRecord, error: docError } = await supabase
        .from('generated_documents')
        .insert({
          una_id: unaData.id,
          document_name: document.document_title,
          document_type: document.document_type,
          is_custom: true,
          custom_content: styledSignedHtml,
          status: 'finalized',
          created_by: profileData.id,
          finalized_by: profileData.id,
          version: newVersion,
          merge_data: {
            una_name: unaData.name,
            signer_name: profileData.full_name,
            signature_date: format(new Date(), 'yyyy-MM-dd'),
            renewal_of: document.id,
          },
          finalized_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (docError) throw docError;

      // 2. Generate PDF
      try {
        const pdfResult = await generateDocumentPdf(docRecord.id, document.document_title);
        await supabase
          .from('generated_documents')
          .update({
            file_url: pdfResult.file_url,
            file_path: pdfResult.file_path,
            file_size: pdfResult.file_size,
          })
          .eq('id', docRecord.id);
      } catch (pdfError) {
        console.warn('PDF generation failed, continuing:', pdfError);
      }

      // 3. Mark old governance document as not current
      await supabase
        .from('governance_documents')
        .update({ is_current_version: false })
        .eq('id', document.id);

      // 4. Create new governance_documents record
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

      const { data: newGovDoc, error: govError } = await supabase
        .from('governance_documents')
        .insert({
          una_id: unaData.id,
          document_type: document.document_type,
          document_category: document.document_category,
          document_title: document.document_title,
          original_file_url: docRecord.file_url || '',
          effective_date: new Date().toISOString().split('T')[0],
          expiration_date: oneYearFromNow.toISOString().split('T')[0],
          access_level: 'all_members',
          created_by: profileData.id,
          version: newVersion,
          is_current_version: true,
          replaces_document_id: document.id,
        })
        .select('id')
        .single();

      if (govError) throw govError;

      // 5. Create journal entry
      const officerRole = unaData.primary_officer_role || 'Secretary Treasurer';
      await supabase
        .from('una_journal_records' as any)
        .insert({
          una_id: unaData.id,
          record_type: 'General Record',
          record_title: `${document.document_title} Renewed (Version ${newVersion})`,
          record_date: new Date().toISOString().split('T')[0],
          activity_description: `${officerRole} ${profileData.full_name} renewed their ${document.document_title}, affirming continued commitment to ecclesiastical principles of stewardship for ${unaData.name}. This is version ${newVersion} of the attestation.`,
          activity_category: 'Governance',
          generated_document_id: docRecord.id,
          authorized_by: profileData.full_name,
          created_by: profileData.id,
        } as any);

      // 6. Create next year's renewal task
      const renewalDueDate = new Date(oneYearFromNow);
      renewalDueDate.setDate(renewalDueDate.getDate() - 30);

      // Mark any existing open renewal tasks completed BEFORE inserting the next-year one,
      // so the new task isn't swept up in the completion query.
      await supabase
        .from('custom_tasks')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('task_title', 'Annual Personal Attestation Renewal')
        .eq('una_id', unaData.id)
        .neq('status', 'completed');

      // Idempotency guard: only insert next-year renewal if no open one exists (should be true after the update above)
      const { data: existingRenewal } = await supabase
        .from('custom_tasks')
        .select('id')
        .eq('una_id', unaData.id)
        .eq('task_title', 'Annual Personal Attestation Renewal')
        .neq('status', 'completed')
        .limit(1)
        .maybeSingle();

      if (!existingRenewal) {
        await supabase.from('custom_tasks').insert({
          una_id: unaData.id,
          user_id: profileData.id,
          task_title: 'Annual Personal Attestation Renewal',
          task_description: `Your Personal Attestation expires on ${format(oneYearFromNow, 'MMMM d, yyyy')}. Renew your commitment by re-signing this foundational document to maintain your ecclesiastical standing.`,
          category_type: 'governance',
          status: 'not_started',
          importance: 'high',
          urgency: 'medium',
          due_date: renewalDueDate.toISOString(),
          recurring_config: {
            enabled: true,
            pattern: 'yearly',
            interval: 1,
            frequency_label: 'Annually',
            governance_document_id: newGovDoc.id,
          },
        });
      }

      setShowSignature(false);
      toast.success('Attestation renewed successfully!', {
        description: `Version ${newVersion} has been filed to governance documents.`,
      });
      onRenewalComplete?.();
    } catch (error) {
      console.error('Renewal error:', error);
      toast.error('Failed to renew attestation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!document) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-amber-800">Document not found.</p>
      </div>
    );
  }

  const expirationStatus = getExpirationStatus();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Attestation Renewal
            </CardTitle>
            <CardDescription>
              Renew your {document.document_title} to maintain ecclesiastical standing
            </CardDescription>
          </div>
          <Badge 
            variant={expirationStatus.color === 'destructive' ? 'destructive' : expirationStatus.color === 'warning' ? 'outline' : 'secondary'}
            className={expirationStatus.color === 'warning' ? 'border-amber-500 text-amber-700 bg-amber-50' : ''}
          >
            {expirationStatus.status === 'expired' && <AlertTriangle className="h-3 w-3 mr-1" />}
            {expirationStatus.status === 'valid' && <CheckCircle className="h-3 w-3 mr-1" />}
            {expirationStatus.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Current Version:</span>
            <span className="ml-2 font-medium">{document.version || 1}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Expires:</span>
            <span className="ml-2 font-medium">
              {document.expiration_date 
                ? format(new Date(document.expiration_date), 'MMMM d, yyyy')
                : 'No expiration set'}
            </span>
          </div>
        </div>

        {expirationStatus.status === 'expired' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
            <div className="text-sm text-red-800">
              <p className="font-medium">Attestation Expired</p>
              <p>Your attestation expired {expirationStatus.days} days ago. Please renew immediately to maintain your ecclesiastical standing.</p>
            </div>
          </div>
        )}

        {expirationStatus.status === 'expiring_soon' && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <Calendar className="h-4 w-4 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Renewal Due Soon</p>
              <p>Your attestation expires in {expirationStatus.days} days. Renew now to ensure continuous compliance.</p>
            </div>
          </div>
        )}

        <Button 
          onClick={() => setShowSignature(true)} 
          disabled={processing}
          className="w-full"
          variant={expirationStatus.status === 'expired' ? 'destructive' : 'default'}
        >
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing Renewal...
            </>
          ) : (
            <>
              <PenTool className="h-4 w-4 mr-2" />
              Renew Attestation (Sign Version {(document.version || 1) + 1})
            </>
          )}
        </Button>
      </CardContent>

      {showSignature && profileData && unaData && (
        <SignatureCapture
          role={unaData.primary_officer_role || "treasury_secretary"}
          signerName={profileData.full_name}
          onSave={handleRenewal}
          onCancel={() => setShowSignature(false)}
          isLoading={processing}
        />
      )}
    </Card>
  );
}

// Export a badge component for use in document lists
export function AttestationExpirationBadge({ expirationDate }: { expirationDate: string | null }) {
  if (!expirationDate) return null;

  const expDate = new Date(expirationDate);
  const daysUntil = differenceInDays(expDate, new Date());

  if (isPast(expDate)) {
    return (
      <Badge variant="destructive" className="text-xs">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Expired
      </Badge>
    );
  }

  if (daysUntil <= 30) {
    return (
      <Badge variant="outline" className="text-xs border-amber-500 text-amber-700 bg-amber-50">
        <Calendar className="h-3 w-3 mr-1" />
        Expires in {daysUntil} days
      </Badge>
    );
  }

  return null;
}
